// Shared mobile bottom-sheet primitive. Two variants:
//
//   peek  — small, no backdrop, body remains interactive (the term sheet).
//   modal — backdrop, focus trap, larger (the TOC sheet).
//
// Owns the cross-cutting behavior so TermSheet, SectionSheet, and the
// future EquationSheet stay presentational. Animation, positioning, and
// family-color styling live in CSS — variant-specific skin classes
// inherit from .maisight-bottom-sheet.
//
// Accessibility:
//   - aria-modal on modal variant
//   - focus moves to first focusable child on open
//   - previously-focused element restored on close
//   - Tab is trapped inside the sheet (modal only — peek lets focus flow
//     out so users can still interact with the prose)
//   - Escape always closes
//
// CSS gates visibility on (max-width: 768px) — desktop never renders the
// sheet visually even when `open` is true (TermSheet on desktop is a
// no-op because the rail already shows the same content).

import { useEffect, useRef, type ReactNode } from "react";

export type BottomSheetVariant = "peek" | "modal";

export interface BottomSheetProps {
  open: boolean;
  variant: BottomSheetVariant;
  onClose: () => void;
  "aria-label": string;
  /** Optional data-term-family for color-keyed skinning (peek term sheet). */
  termFamily?: string;
  className?: string;
  children: ReactNode;
}

export function BottomSheet({
  open, variant, onClose, "aria-label": ariaLabel, termFamily, className,
  children,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const first = sheetRef.current?.querySelector<HTMLElement>(
      'button, [href], [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    return () => {
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (variant !== "modal" || e.key !== "Tab") return;
      const focusables = sheetRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, variant, onClose]);

  if (!open) return null;
  const skin = className ? ` ${className}` : "";
  return (
    <>
      {variant === "modal" && (
        <div
          className="maisight-bottom-sheet-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        ref={sheetRef}
        className={`maisight-bottom-sheet maisight-bottom-sheet--${variant}${skin}`}
        role="dialog"
        aria-label={ariaLabel}
        aria-modal={variant === "modal"}
        data-term-family={termFamily}
      >
        {children}
      </div>
    </>
  );
}
